import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, Briefcase, Users, Clock, AlertCircle, 
  Mail, FileText, Phone, Calendar, Settings, X, Check,
  ChevronRight, Bell, TrendingUp, Calculator, ClipboardList,
  MapPin, Sun, Sunrise, CheckCircle2, ArrowUpRight, ArrowDownRight,
  Navigation, Loader, Play, Route
} from 'lucide-react';
import { useBusinessContext } from '../contexts/BusinessContext';
import JobDetailDrawer from '../components/jobs/JobDetailDrawer';
import JobFormModal from '../components/jobs/JobFormModal';
import CompleteJobModal from '../components/jobs/CompleteJobModal';
import api, { serviceTypesApi, taxConfigApi, reportsApi } from '../services/api';
import TrialBanner from '../components/TrialBanner';
import dashboardService from '../services/dashboardService';
import routingService from '../services/routingService';
import TaxSetupWizard from '../components/TaxSetupWizard';
import { formatCurrency } from '../utils/helpers';


// Helper: Get local date string in YYYY-MM-DD format (NOT UTC)
const getLocalDateString = (date = new Date()) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Helper to transform API job data for dashboard display
const transformJobForDashboard = (job) => ({
  ...job,
  // Support both dashboard DTO (flat fields) and full job objects (nested)
  customerName: job.customerName || job.customer?.name || 'Unknown Customer',
  serviceType: job.serviceTypes || (job.jobServices?.length > 0
    ? job.jobServices.map(js => js.serviceType?.name).filter(Boolean).join(', ')
    : 'No services specified'),
});
// Get greeting based on time of day
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good morning', icon: Sunrise };
  if (hour < 17) return { text: 'Good afternoon', icon: Sun };
  return { text: 'Good evening', icon: Sun };
};

// Format time from ISO date
const formatJobTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0) return null;
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Check if date is today (uses local time)
const isToday = (dateString) => {
  if (!dateString) return false;
  // Compare just the date portion in local timezone
  const jobDate = dateString.split('T')[0];
  const todayStr = getLocalDateString();
  return jobDate === todayStr;
};

// Check if date is tomorrow
const isTomorrow = (dateString) => {
  if (!dateString) return false;
  const jobDate = dateString.split('T')[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return jobDate === getLocalDateString(tomorrow);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { businessName, userRole, isOwner, isEmployee, user } = useBusinessContext();
  const [loading, setLoading] = useState(true);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  
  // Start My Day state
  const [showStartMyDayModal, setShowStartMyDayModal] = useState(false);
  const [startMyDayLoading, setStartMyDayLoading] = useState(false);
  const [startMyDayRoute, setStartMyDayRoute] = useState(null);
  const [startMyDayError, setStartMyDayError] = useState(null);
  const [gpsStatus, setGpsStatus] = useState(null); // 'getting' | 'success' | 'failed'
  const [selectedJob, setSelectedJob] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Edit modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    sendJobConfirmation: false,
    sendServicePrep: false,
    sendJobReminder: false,
    sendJobCompleted: false,
    sendReviewRequest: false,
    sendPaymentReceived: false,
  });

  // Complete modal state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingJob, setCompletingJob] = useState(null);
  const [completeFormData, setCompleteFormData] = useState({
    finalAmount: '',
    completedDate: new Date().toISOString().split('T')[0],
    notes: '',
    sendCompletedEmail: false,
    paidOnSite: false,
    paymentMethod: 'Cash',
    paymentReference: '',
    sendReceiptEmail: false,
    salesTaxCollected: false,
    transactionType: 'Business',
  });
  const [taxSettings, setTaxSettings] = useState({
    collectsSalesTax: false,
    salesTaxRate: 0,
  });

  // Shape dashboard job into full job object for drawer/modals
  const shapeJobForDrawer = (job) => ({
    ...job,
    customer: job.customer || {
      id: job.customerId,
      name: job.customerName || 'Unknown Customer',
    },
    jobServices: job.jobServices || (job.serviceTypes
      ? [{ serviceType: { name: job.serviceTypes } }]
      : []),
  });

  const handleJobClick = (job) => {
    setSelectedJob(shapeJobForDrawer(job));
    setDrawerOpen(true);
  };

  const handleDashboardEdit = async (job) => {
    setDrawerOpen(false);
    try {
      // Fetch full job data so JobFormModal can pre-populate correctly
      const response = await api.get(`/jobs/${job.id}`);
      setEditingJob(response.data);
    } catch (err) {
      console.error('Failed to load job for editing:', err);
      setEditingJob(shapeJobForDrawer(job));
    }
    setShowFormModal(true);
  };

  const handleDashboardComplete = async (job) => {
    setDrawerOpen(false);
    let fullJob;
    try {
      const response = await api.get(`/jobs/${job.id}`);
      fullJob = response.data;
    } catch (err) {
      fullJob = shapeJobForDrawer(job);
    }
    setCompletingJob(fullJob);
    setCompleteFormData({
      finalAmount: fullJob.quoteAmount?.toString() || '',
      completedDate: new Date().toISOString().split('T')[0],
      notes: fullJob.notes || '',
      sendCompletedEmail: false,
      paidOnSite: false,
      paymentMethod: 'Cash',
      paymentReference: '',
      sendReceiptEmail: false,
      salesTaxCollected: false,
      transactionType: 'Business',
    });
    setShowCompleteModal(true);
  };
  
  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  // Redirect platform admins to admin portal
  useEffect(() => {
    const role = user?.role || userRole;
    if (role === 'ApplicationAdmin' || role === 'Support') {
      navigate('/admin', { replace: true });
    }
  }, [user, userRole, navigate]);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState({
    // Needs Attention
    newLeadsCount: 0,
    overdueInvoicesCount: 0,
    overdueInvoicesAmount: 0,
    readyToInvoiceCount: 0,
    followUpsDueCount: 0,
    quotesPendingCount: 0,
    quotesPendingAmount: 0,
    
    // Stats
    totalRevenue: 0,
    revenueThisWeek: 0,
    revenueLastWeek: 0,
    activeServiceCalls: 0,
    totalClients: 0,
    pendingInvoices: 0,
    scheduledRevenue: 0,
    outstandingBalance: 0,
    jobsCompletedThisWeek: 0,
    
    // Lists
    todaysJobs: [],
    upcomingJobs: [],
    recentCompletedJobs: [],
  });

  const [taxReminders, setTaxReminders] = useState([]);
  const [showTaxWizard, setShowTaxWizard] = useState(false);
  const [taxStatusChecked, setTaxStatusChecked] = useState(false);

  // Widget preferences
  const [preferences, setPreferences] = useState({
    showNeedsAttention: true,
    showTodaysSchedule: true,
    showWeeklyStats: true,
    showOutstandingBalance: true,
    showScheduledRevenue: true,
    showActiveServiceCalls: true,
    showTotalClients: true,
    showUpcomingJobs: true,
    showRecentCompleted: true,
    showQuotesPending: true,
    showQuickActions: true,
  });

  useEffect(() => {
    loadDashboardData();
    if (isOwner) {
      loadPreferences();
      loadModalData();
    }
  }, [userRole]);

  // Load tax reminders for Owner
  useEffect(() => {
    const loadTaxReminders = async () => {
      if (!isOwner) return;
      try {
        const response = await reportsApi.getTaxReminders();
        setTaxReminders(response.data.reminders || []);
      } catch (error) {
        console.warn('Could not load tax reminders:', error);
      }
    };
    loadTaxReminders();
  }, [isOwner]);

  // Check tax setup wizard
  useEffect(() => {
    const checkTaxStatus = async () => {
      if (!isOwner || taxStatusChecked) return;
      try {
        const response = await taxConfigApi.getStatus();
        const { isSetupComplete, isDismissed } = response.data;
        if (!isSetupComplete && !isDismissed) {
          setShowTaxWizard(true);
        }
      } catch (error) {
        console.error('Error checking tax status:', error);
      } finally {
        setTaxStatusChecked(true);
      }
    };
    checkTaxStatus();
  }, [isOwner, taxStatusChecked]);

  const loadPreferences = async () => {
    try {
      const prefs = await dashboardService.getPreferences();
      setPreferences(prev => ({ ...prev, ...prefs }));
    } catch (error) {
      console.warn('Could not load preferences, using defaults:', error);
    }
  };

  const loadModalData = async () => {
    try {
      const [customersRes, serviceTypesRes, emailRes] = await Promise.all([
        api.get('/customers'),
        serviceTypesApi.getAll(),
        api.get('/business/email-settings'),
      ]);
      setCustomers(customersRes.data || []);
      setServiceTypes(serviceTypesRes.data || []);
      setEmailSettings(emailRes.data || {});

      try {
        const taxRes = await taxConfigApi.getConfiguration();
        if (taxRes.data) {
          setTaxSettings({
            collectsSalesTax: taxRes.data.collectsSalesTax || false,
            salesTaxRate: taxRes.data.salesTaxRate || 0,
          });
        }
      } catch {}
    } catch (err) {
      console.warn('Could not load modal data:', err);
    }
  };

  const savePreferences = async (newPrefs) => {
    try {
      await dashboardService.updatePreferences(newPrefs);
      setPreferences(newPrefs);
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      if (isOwner) {
        const [jobsData, summaryData, statsData] = await Promise.all([
          dashboardService.getJobs(),
          dashboardService.getSummary(),
          dashboardService.getStats(),
        ]);

        setDashboardData({
          // From getJobs()
          todaysJobs: (jobsData.todaysJobs || []).map(transformJobForDashboard),
          upcomingJobs: (jobsData.upcomingJobs || []).map(transformJobForDashboard),
          recentCompletedJobs: (jobsData.recentlyCompleted || []).map(transformJobForDashboard),
          activeServiceCalls: jobsData.activeJobsCount || 0,
          jobsCompletedThisWeek: jobsData.jobsCompletedThisWeek || 0,
          totalClients: statsData.totalCustomers || 0,

          // From getSummary()
          newLeadsCount: summaryData.newLeadsCount || 0,
          overdueInvoicesCount: summaryData.overdueInvoicesCount || 0,
          overdueInvoicesAmount: summaryData.overdueInvoicesAmount || 0,
          readyToInvoiceCount: summaryData.readyToInvoiceCount || 0,
          followUpsDueCount: summaryData.followUpsDueCount || 0,
          quotesPendingCount: summaryData.quotesPendingCount || 0,
          quotesPendingAmount: summaryData.quotesPendingAmount || 0,

          // From getStats()
          revenueThisWeek: statsData.revenueThisWeek || 0,
          revenueLastWeek: statsData.revenueLastWeek || 0,
          outstandingBalance: statsData.outstandingBalance || 0,
          scheduledRevenue: statsData.scheduledRevenue || 0,

          // Unused
          totalRevenue: 0,
          pendingInvoices: 0,
        });

      } else {
        const jobsData = await dashboardService.getJobs();

        setDashboardData(prev => ({
          ...prev,
          todaysJobs: (jobsData.todaysJobs || []).map(transformJobForDashboard),
          upcomingJobs: (jobsData.upcomingJobs || []).map(transformJobForDashboard),
          recentCompletedJobs: (jobsData.recentlyCompleted || []).map(transformJobForDashboard),
          activeServiceCalls: jobsData.activeJobsCount || 0,
          jobsCompletedThisWeek: jobsData.jobsCompletedThisWeek || 0,
          totalClients: 0,
        }));
      }

    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // START MY DAY FUNCTIONS
  // ==========================================
  
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  };

  const handleStartMyDay = async () => {
    setShowStartMyDayModal(true);
    setStartMyDayLoading(true);
    setStartMyDayError(null);
    setStartMyDayRoute(null);
    setGpsStatus('getting');

    try {
      // Step 1: Get GPS location
      let startLocation = null;
      try {
        startLocation = await getCurrentLocation();
        setGpsStatus('success');
      } catch (gpsError) {
        console.warn('GPS not available:', gpsError);
        setGpsStatus('failed');
        // Continue without GPS
      }

      // Step 2: Optimize today's route
    const routeDate = getLocalDateString();
      const response = await routingService.optimizeRoute(
        routeDate,
        'Scheduled',
        null, // Use business default priority
        startLocation
);

      if (response.data.optimizedJobs?.length === 0) {
        setStartMyDayError('No jobs scheduled for today');
      } else {
        setStartMyDayRoute(response.data);
      }
    } catch (error) {
      console.error('Start My Day error:', error);
      setStartMyDayError('Failed to optimize route. Please try again.');
    } finally {
      setStartMyDayLoading(false);
    }
  };

  const handleNavigateRoute = async () => {
    if (!startMyDayRoute?.optimizedJobs?.length) return;

    try {
      const response = await routingService.getNavigationUrls(startMyDayRoute.optimizedJobs);
      const { googleMapsUrl } = response.data;
      
      if (googleMapsUrl) {
        window.open(googleMapsUrl, '_blank');
      }
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback: build URL manually
      const jobs = startMyDayRoute.optimizedJobs;
      const waypoints = jobs.map(j => `${j.latitude},${j.longitude}`).join('/');
      const fallbackUrl = `https://www.google.com/maps/dir/${waypoints}`;
      window.open(fallbackUrl, '_blank');
    }
  };

  // Calculate total needs attention
  const totalNeedsAttention = 
    dashboardData.newLeadsCount + 
    dashboardData.overdueInvoicesCount + 
    dashboardData.readyToInvoiceCount + 
    dashboardData.followUpsDueCount +
    dashboardData.quotesPendingCount;

  // Revenue trend
  const revenueTrend = dashboardData.revenueLastWeek > 0
    ? ((dashboardData.revenueThisWeek - dashboardData.revenueLastWeek) / dashboardData.revenueLastWeek * 100)
    : 0;

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Header */}
      <div style={{background: 'linear-gradient(135deg, #0f172a, #1e3a5f)'}} className="text-white">
        <div className="p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GreetingIcon size={24} className="text-primary-200 hidden sm:block" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold">{greeting.text}</h1>
                <p className="text-primary-200 text-sm">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              </div>
            </div>
            
            {isOwner && (
              <button
                onClick={() => setShowPreferencesModal(true)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                title="Customize Dashboard"
              >
                <Settings size={20} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 lg:p-6 pb-24 lg:pb-8 space-y-4 lg:space-y-6">
        
        {/* Trial Banner */}
        {isOwner && <TrialBanner />}

        {/* ========================================== */}
        {/* START MY DAY BUTTON - Top Priority */}
        {/* ========================================== */}
        {dashboardData.todaysJobs.length > 0 && (
          <button
            onClick={handleStartMyDay}
            className="w-full text-white rounded-xl p-4 shadow-lg hover:opacity-95 transition-all flex items-center justify-between group"
            style={{background: 'linear-gradient(135deg, #14532d, #166534)'}}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play size={24} className="ml-0.5" />
              </div>
              <div className="text-left">
                <p className="text-lg font-bold">Start My Day</p>
                <p className="text-green-100 text-sm">
                  {dashboardData.todaysJobs.length} job{dashboardData.todaysJobs.length !== 1 ? 's' : ''} • Tap to optimize & navigate
                </p>
              </div>
            </div>
            <ChevronRight size={24} className="text-green-200 group-hover:translate-x-1 transition-transform" />
          </button>
        )}

        {/* ========================================== */}
        {/* TODAY'S SCHEDULE - Priority for Field Service */}
        {/* ========================================== */}
        {preferences.showTodaysSchedule && (
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between" style={{background: 'linear-gradient(135deg, #0f172a, #1e3a5f)'}}>
              <div className="flex items-center gap-2 text-white">
                <Calendar size={18} />
                <span className="font-semibold">Today's Schedule</span>
                {dashboardData.todaysJobs.length > 0 && (
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                    {dashboardData.todaysJobs.length} job{dashboardData.todaysJobs.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {dashboardData.todaysJobs.length > 0 && (
                <button
                  onClick={() => navigate('/jobs?status=today')}
                  className="text-white/80 hover:text-white text-sm flex items-center gap-1"
                >
                  View all <ChevronRight size={16} />
                </button>
              )}
            </div>
            
            <div className="p-4">
              {dashboardData.todaysJobs.length === 0 ? (
                <div className="flex items-center gap-3 text-gray-500">
                  <CheckCircle2 size={20} className="text-green-500" />
                  <span className="text-sm">No jobs scheduled for today</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {dashboardData.todaysJobs.slice(0, 4).map((job, index) => (
                    <div
                      key={job.id}
                      onClick={() => handleJobClick(job)}
                      className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate text-sm">{job.customerName}</p>
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <MapPin size={10} />
                          {job.address || job.serviceType}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium text-gray-900">
                          {formatJobTime(job.scheduledDate) || 'Time TBD'}
                        </p>
                        {/* OWNER ONLY: Show quote amount */}
                        {isOwner && job.quoteAmount > 0 && (
                          <p className="text-xs text-gray-500">{formatCurrency(job.quoteAmount)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {dashboardData.todaysJobs.length > 4 && (
                    <button
                      onClick={() => navigate('/jobs?status=today')}
                      className="w-full text-center text-sm text-primary-600 hover:text-primary-700 py-2"
                    >
                      + {dashboardData.todaysJobs.length - 4} more jobs
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================== */}
        {/* NEEDS ATTENTION - Compact Version */}
        {/* ========================================== */}
        {isOwner && preferences.showNeedsAttention && (
          <>
            {totalNeedsAttention > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-gray-700">Needs Attention</span>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {dashboardData.quotesPendingCount > 0 && preferences.showQuotesPending && (
                    <NeedsAttentionCard
                      icon={ClipboardList}
                      count={dashboardData.quotesPendingCount}
                      label="Quotes"
                      subtext={dashboardData.quotesPendingAmount > 0 ? `${formatCurrency(dashboardData.quotesPendingAmount)}` : null}
                      color="purple"
                      onClick={() => navigate('/jobs?mode=list&view=needs-attention')}
                    />
                  )}
                  {dashboardData.newLeadsCount > 0 && (
                    <NeedsAttentionCard
                      icon={Mail}
                      count={dashboardData.newLeadsCount}
                      label="New Leads"
                      color="blue"
                      onClick={() => navigate('/leads')}
                    />
                  )}
                  {dashboardData.overdueInvoicesCount > 0 && (
                    <NeedsAttentionCard
                      icon={AlertCircle}
                      count={dashboardData.overdueInvoicesCount}
                      label="Overdue"
                      subtext={`${formatCurrency(dashboardData.overdueInvoicesAmount)}`}
                      color="red"
                      onClick={() => navigate('/invoices')}
                    />
                  )}
                  {dashboardData.readyToInvoiceCount > 0 && (
                    <NeedsAttentionCard
                      icon={FileText}
                      count={dashboardData.readyToInvoiceCount}
                      label="To Invoice"
                      color="green"
                      onClick={() => navigate('/invoices')}
                    />
                  )}
                  {dashboardData.followUpsDueCount > 0 && (
                    <NeedsAttentionCard
                      icon={Phone}
                      count={dashboardData.followUpsDueCount}
                      label="Follow-ups"
                      color="amber"
                      onClick={() => navigate('/leads')}
                    />
                  )}
                </div>
              </div>
            ) : (
              /* Compact "All caught up" */
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                <Check size={16} className="text-green-600" />
                <span className="text-sm text-green-800">All caught up! No urgent items.</span>
              </div>
            )}
          </>
        )}

        {/* Tax Reminders - Compact */}
        {isOwner && taxReminders.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
            <Calculator size={16} className="text-indigo-600 flex-shrink-0" />
            <span className="text-sm text-indigo-800 truncate">
              Tax deadline: {taxReminders[0].title} - {new Date(taxReminders[0].dueDate).toLocaleDateString()}{taxReminders[0].estimatedAmount > 0 && ` - about ${formatCurrency(taxReminders[0].estimatedAmount)}`}
            </span>
            <button
              onClick={() => navigate('/reports', { state: { activeCategory: 'tax', activeReport: 'quarterly' } })}
              className="ml-auto text-indigo-600 hover:text-indigo-700 text-sm font-medium flex-shrink-0"
            >
              View
            </button>
          </div>
        )}

        {/* ========================================== */}
        {/* STATS GRID - Owner */}
        {/* ========================================== */}
        {isOwner && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Weekly Revenue with Trend */}
            {preferences.showWeeklyStats && (
              <StatCard
                label="This Week"
                value={formatCurrency(dashboardData.revenueThisWeek)}
                icon={DollarSign}
                color="green"
                trend={revenueTrend}
                onClick={() => navigate('/reports')}
              />
            )}
            
            {preferences.showOutstandingBalance && (
              <StatCard
                label="Outstanding"
                value={formatCurrency(dashboardData.outstandingBalance)}
                icon={AlertCircle}
                color="amber"
                onClick={() => navigate('/invoices')}
              />
            )}
            
            {preferences.showScheduledRevenue && (
              <StatCard
                label="Pipeline"
                value={formatCurrency(dashboardData.scheduledRevenue)}
                icon={TrendingUp}
                color="indigo"
                onClick={() => navigate('/jobs')}
              />
            )}
            
            {preferences.showActiveServiceCalls && (
              <StatCard
                label="Active Jobs"
                value={dashboardData.activeServiceCalls}
                icon={Briefcase}
                color="blue"
                subtext={`${dashboardData.jobsCompletedThisWeek} done this week`}
                onClick={() => navigate('/jobs?mode=list&view=scheduled')}
              />
            )}
          </div>
        )}

        {/* ========================================== */}
        {/* EMPLOYEE VIEW */}
        {/* ========================================== */}
        {isEmployee && (
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label="Active Jobs"
              value={dashboardData.activeServiceCalls}
              icon={Briefcase}
              color="blue"
              onClick={() => navigate('/jobs')}
            />
            <StatCard
              label="Completed"
              value={dashboardData.jobsCompletedThisWeek}
              icon={CheckCircle2}
              color="green"
              subtext="this week"
              onClick={() => navigate('/jobs?status=completed')}
            />
          </div>
        )}

        {/* ========================================== */}
        {/* JOB LISTS - Two Column */}
        {/* ========================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Upcoming Jobs */}
          {(isEmployee || preferences.showUpcomingJobs) && dashboardData.upcomingJobs.length > 0 && (
            <JobList
              title="📅 Upcoming"
              jobs={dashboardData.upcomingJobs}
              emptyText="No upcoming jobs"
              onViewAll={() => navigate('/jobs?status=scheduled')}
              showDate
              isOwner={isOwner}
              onJobClick={handleJobClick}
            />
          )}

          {/* Needs Invoicing - completed but not yet invoiced */}
          {isOwner && preferences.showRecentCompleted && dashboardData.recentCompletedJobs.length > 0 && (
            <JobList
              title="🧾 Needs Invoicing"
              jobs={dashboardData.recentCompletedJobs}
              emptyText="All caught up on invoicing"
              onViewAll={() => navigate('/invoices')}
              showDate
              dateField="completedDate"
              statusColor="green"
              isOwner={isOwner}
              onJobClick={handleJobClick}
            />
          )}
        </div>

        {/* ========================================== */}
        {/* QUICK ACTIONS - Compact */}
        {/* ========================================== */}
        {preferences.showQuickActions && (
          <div className="flex flex-wrap gap-2">
            {/* OWNER ONLY: New Job button */}
            {isOwner && (
              <button onClick={() => navigate('/jobs')} className="btn btn-primary btn-sm">
                + New Job
              </button>
            )}
            <button onClick={() => navigate('/customers')} className="btn btn-secondary btn-sm">
              + Customer
            </button>
            {isOwner && (
              <>
                {dashboardData.quotesPendingCount > 0 && (
                  <button onClick={() => navigate('/jobs?mode=list&view=needs-attention')} className="btn btn-secondary btn-sm">
                    📝 {dashboardData.quotesPendingCount} Quotes
                  </button>
                )}
                <button onClick={() => navigate('/reports')} className="btn btn-secondary btn-sm">
                  📊 Reports
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* ========================================== */}
      {/* START MY DAY MODAL */}
      {/* ========================================== */}
      {showStartMyDayModal && (
        <StartMyDayModal
          loading={startMyDayLoading}
          route={startMyDayRoute}
          error={startMyDayError}
          gpsStatus={gpsStatus}
          isOwner={isOwner}
          onNavigate={handleNavigateRoute}
          onClose={() => setShowStartMyDayModal(false)}
          onOpenRoutePlanner={() => {
            setShowStartMyDayModal(false);
            navigate('/jobs?view=map');
          }}
        />
      )}

      {/* Preferences Modal */}
      {showPreferencesModal && (
        <PreferencesModal
          preferences={preferences}
          onSave={(prefs) => {
            savePreferences(prefs);
            setShowPreferencesModal(false);
          }}
          onClose={() => setShowPreferencesModal(false)}
        />
      )}

      {/* Tax Setup Wizard */}
      <TaxSetupWizard
        isOpen={showTaxWizard}
        onClose={() => setShowTaxWizard(false)}
        onComplete={() => setShowTaxWizard(false)}
      />

      {/* Job Detail Drawer */}
      <JobDetailDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedJob(null); }}
        job={selectedJob}
        onEdit={handleDashboardEdit}
        onDelete={() => { setDrawerOpen(false); }}
        onComplete={handleDashboardComplete}
        onSendPrepEmail={() => {}}
        onSendQuoteEmail={() => {}}
        sendingEmail={null}
        sendingQuoteEmail={null}
      />

      {/* Job Form Modal */}
      <JobFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setEditingJob(null); }}
        onSubmit={async (submitData) => {
          const { formData, jobServices, totalQuoteAmount, emailOverrides, quoteBreakdownJson } = submitData;
          const payload = {
            customerId: formData.customerId,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            status: formData.status,
            scheduledDate: formData.scheduledDate,
            quoteAmount: totalQuoteAmount,
            estimatedDurationMinutes: formData.estimatedDurationMinutes || null,
            description: formData.description,
            quoteBreakdownJson,
            sendConfirmationEmail: emailOverrides?.sendConfirmationEmail || false,
          };
          const validServices = jobServices.filter(s => s.serviceTypeId).map(s => ({
            serviceTypeId: parseInt(s.serviceTypeId),
            quantity: s.quantity || 1,
            unitPrice: s.unitPrice || null,
            totalPrice: s.totalPrice || 0,
            notes: s.notes || null,
            quoteBreakdownJson: s.quoteBreakdownJson || null,
          }));
          if (editingJob) {
            await api.put(`/jobs/${editingJob.id}`, { ...payload, id: editingJob.id, businessId: editingJob.businessId });
            if (validServices.length > 0) {
              await api.post(`/jobservices/job/${editingJob.id}/set`, { services: validServices });
            }
          }
          setShowFormModal(false);
          setEditingJob(null);
          loadDashboardData();
        }}
        editingJob={editingJob}
        customers={customers}
        serviceTypes={serviceTypes}
        emailSettings={emailSettings}
        isOwner={isOwner}
      />

      {/* Complete Job Modal */}
      <CompleteJobModal
        isOpen={showCompleteModal}
        onClose={() => { setShowCompleteModal(false); setCompletingJob(null); loadDashboardData(); }}
        job={completingJob}
        formData={completeFormData}
        setFormData={setCompleteFormData}
        onSubmit={async () => {
          if (!completingJob) return;
          await api.post(`/jobs/${completingJob.id}/complete`, {
            finalAmount: completeFormData.finalAmount ? parseFloat(completeFormData.finalAmount) : null,
            completedDate: completeFormData.completedDate
              ? new Date(completeFormData.completedDate + 'T12:00:00').toISOString()
              : new Date().toISOString(),
            notes: completeFormData.notes || null,
            sendCompletedEmail: completeFormData.paidOnSite ? false : completeFormData.sendCompletedEmail,
            paidOnSite: completeFormData.paidOnSite || false,
            paymentMethod: completeFormData.paymentMethod || 'Cash',
            paymentReference: completeFormData.paymentReference || null,
            sendReceiptEmail: completeFormData.paidOnSite ? completeFormData.sendReceiptEmail : false,
            salesTaxCollected: completeFormData.paidOnSite ? (completeFormData.salesTaxCollected || false) : false,
            transactionType: completeFormData.transactionType || 'Business',
          });
          setShowCompleteModal(false);
          setCompletingJob(null);
          loadDashboardData();
        }}
        emailSettings={emailSettings}
        taxSettings={taxSettings}
        remainingJobsToday={[]}
        onNavigateToNext={() => {}}
        onReoptimize={() => {}}
      />
    </div>
  );
};

// ========================================== 
// COMPONENT: Start My Day Modal
// ==========================================
const StartMyDayModal = ({ loading, route, error, gpsStatus, isOwner, onNavigate, onClose, onOpenRoutePlanner }) => {
  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Route size={22} />
              </div>
              <div>
                <h2 className="text-lg font-bold">Start My Day</h2>
                <p className="text-green-100 text-sm">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white p-1">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {loading ? (
            <div className="text-center py-8">
              <Loader size={40} className="mx-auto text-green-500 animate-spin mb-4" />
              <p className="text-gray-600 font-medium">
                {gpsStatus === 'getting' ? 'Getting your location...' : 'Optimizing your route...'}
              </p>
              <p className="text-sm text-gray-400 mt-1">This may take a moment</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar size={28} className="text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Close
              </button>
            </div>
          ) : route ? (
            <>
              {/* GPS Status */}
              <div className={`flex items-center gap-2 text-sm mb-4 px-3 py-2 rounded-lg ${
                gpsStatus === 'success' 
                  ? 'bg-green-50 text-green-700' 
                  : 'bg-amber-50 text-amber-700'
              }`}>
                <MapPin size={14} />
                <span>
                  {gpsStatus === 'success' 
                    ? 'Route optimized from your current location' 
                    : 'Route optimized (GPS unavailable)'}
                </span>
              </div>

              {/* Route Summary */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{route.optimizedJobs?.length || 0}</p>
                  <p className="text-xs text-gray-500">Jobs</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{route.totalDistance?.toFixed(1) || 0}</p>
                  <p className="text-xs text-gray-500">Miles</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{formatDuration(route.totalDurationMinutes)}</p>
                  <p className="text-xs text-gray-500">Est. Time</p>
                </div>
              </div>

              {/* OWNER ONLY: Revenue Stats */}
              {isOwner && route.totalRevenue > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700">Today's Revenue</p>
                      <p className="text-2xl font-bold text-green-800">{formatCurrency(route.totalRevenue)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-700">$/Hour</p>
                      <p className="text-xl font-bold text-green-800">{formatCurrency(route.revenuePerHour)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Job List Preview */}
              <div className="mb-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Route Order</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {route.optimizedJobs?.slice(0, 5).map((job, index) => (
                    <div key={job.jobId} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{job.customerName}</p>
                        <p className="text-xs text-gray-500 truncate">{job.address}</p>
                      </div>
                      {job.distanceFromPrevious > 0 && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {job.distanceFromPrevious.toFixed(1)} mi
                        </span>
                      )}
                    </div>
                  ))}
                  {route.optimizedJobs?.length > 5 && (
                    <p className="text-xs text-gray-400 text-center py-1">
                      + {route.optimizedJobs.length - 5} more stops
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={onNavigate}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 shadow-lg"
                >
                  <Navigation size={20} />
                  Navigate in Maps
                </button>
                <button
                  onClick={onOpenRoutePlanner}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-xl text-sm"
                >
                  Open Route Planner for more options
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ========================================== 
// COMPONENT: Needs Attention Card (Compact)
// ==========================================
const NeedsAttentionCard = ({ icon: Icon, count, label, subtext, color, onClick }) => {
  const colors = {
    purple: 'text-purple-200',
    blue: 'text-blue-200',
    red: 'text-red-200',
    green: 'text-green-200',
    amber: 'text-amber-200',
  };

  const gradients = {
    purple: 'linear-gradient(135deg, #1e1b4b, #312e81)',
    blue: 'linear-gradient(135deg, #0f172a, #1e3a8a)',
    red: 'linear-gradient(135deg, #450a0a, #7f1d1d)',
    green: 'linear-gradient(135deg, #052e16, #14532d)',
    amber: 'linear-gradient(135deg, #422006, #78350f)',
  };

  const iconColors = {
    purple: 'bg-purple-400',
    blue: 'bg-blue-400',
    red: 'bg-red-400',
    green: 'bg-green-400',
    amber: 'bg-amber-400',
  };

  return (
    <div
      onClick={onClick}
      className="p-3 rounded-lg cursor-pointer hover:opacity-90 transition-all"
      style={{ background: gradients[color] }}
    >
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 ${iconColors[color]} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className="text-white" size={16} />
        </div>
        <div className="min-w-0">
          <p className={`text-lg font-bold leading-none ${colors[color]}`}>{count}</p>
          <p className="text-xs text-white/70 truncate">{label}</p>
          {subtext && <p className="text-xs text-white/50 truncate">{subtext}</p>}
        </div>
      </div>
    </div>
  );
};

// ========================================== 
// COMPONENT: Stat Card
// ==========================================
const StatCard = ({ label, value, icon: Icon, color, trend, subtext, onClick }) => {
  const iconColors = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    indigo: 'bg-indigo-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
  };

  return (
    <div
      onClick={onClick}
      className="card cursor-pointer hover:shadow-md transition-all p-3"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500 truncate">{label}</p>
          <p className="text-lg lg:text-xl font-bold text-gray-900 mt-0.5">{value}</p>
          {trend !== undefined && trend !== 0 && (
            <div className={`flex items-center gap-1 text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
              {Math.abs(trend).toFixed(0)}% vs last week
            </div>
          )}
          {subtext && !trend && (
            <p className="text-xs text-gray-500 mt-1">{subtext}</p>
          )}
        </div>
        <div className={`w-9 h-9 ${iconColors[color]} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className="text-white" size={18} />
        </div>
      </div>
    </div>
  );
};

// ========================================== 
// COMPONENT: Job List
// ==========================================
const JobList = ({ title, jobs, emptyText, onViewAll, showDate, dateField = 'scheduledDate', statusColor = 'blue', isOwner, onJobClick }) => {
  const navigate = useNavigate();
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    if (isToday(dateString)) return 'Today';
    if (isTomorrow(dateString)) return 'Tomorrow';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const statusColors = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <button onClick={onViewAll} className="text-primary-600 hover:text-primary-700 text-sm">
          View all
        </button>
      </div>

      {jobs.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {jobs.map(job => (
            <div
              key={job.id}
              onClick={() => onJobClick ? onJobClick(job) : navigate('/jobs')}
              className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              <div className={`w-8 h-8 ${statusColor === 'green' ? 'bg-green-100' : 'bg-blue-100'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Briefcase className={statusColor === 'green' ? 'text-green-600' : 'text-blue-600'} size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate text-sm">{job.customerName}</p>
                <p className="text-xs text-gray-500 truncate">{job.serviceType}</p>
              </div>
              {showDate && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColors[statusColor]}`}>
                  {formatDate(job[dateField])}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================================== 
// COMPONENT: Preferences Modal
// ==========================================
const PreferencesModal = ({ preferences, onSave, onClose }) => {
  const [localPrefs, setLocalPrefs] = useState(preferences);

  const options = [
    { key: 'showTodaysSchedule', label: "Today's Schedule", description: 'Jobs scheduled for today' },
    { key: 'showNeedsAttention', label: 'Needs Attention', description: 'Urgent items requiring action' },
    { key: 'showWeeklyStats', label: 'Weekly Revenue', description: 'This week vs last week' },
    { key: 'showOutstandingBalance', label: 'Outstanding Balance', description: 'Unpaid invoices' },
    { key: 'showScheduledRevenue', label: 'Pipeline', description: 'Scheduled job value' },
    { key: 'showActiveServiceCalls', label: 'Active Jobs', description: 'Current job count' },
    { key: 'showUpcomingJobs', label: 'Upcoming Jobs', description: 'Next scheduled jobs' },
    { key: 'showRecentCompleted', label: 'Needs Invoicing', description: 'Completed jobs awaiting invoice' },
    { key: 'showQuickActions', label: 'Quick Actions', description: 'Action buttons' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Customize Dashboard</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {options.map(item => (
              <label key={item.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={localPrefs[item.key]}
                  onChange={e => setLocalPrefs({ ...localPrefs, [item.key]: e.target.checked })}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex gap-3">
          <button onClick={() => onSave(localPrefs)} className="btn btn-primary flex-1">
            Save
          </button>
          <button onClick={onClose} className="btn btn-secondary flex-1">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;